// packages/frontend/src/stores/fileStore.ts
import { create } from 'zustand'
import { api, ApiError } from '../utils/api'
import { useToastStore } from './toastStore'
import { useAuthStore } from './authStore'
import type { FileSystemItem } from '@shared'
import { WORKER_URL as API_BASE_URL } from '../config'

interface UploadProgress {
  loaded: number
  total: number
  percentage: number
  speed: number
  eta: number
}

interface FileState {
  currentPath: string
  items: FileSystemItem[]
  isLoading: boolean
  selectedItems: Set<string>
  viewMode: 'grid' | 'list'
  sortBy: 'name' | 'date' | 'size' | 'type'
  sortOrder: 'asc' | 'desc'
  
  // Actions
  loadItems: (path?: string) => Promise<void>
  navigateToFolder: (path: string) => Promise<void>
  createFolder: (name: string, isPublic: boolean) => Promise<void>
  uploadFile: (file: File, isPublic: boolean, fileName?: string) => Promise<any>
  uploadFileWithProgress: (
    file: File, 
    isPublic: boolean, 
    onProgress: (loaded: number, total: number) => void,
    signal?: AbortSignal,
    fileName?: string
  ) => Promise<any>
  uploadChunkedFile: (
    file: File,
    isPublic: boolean,
    onProgress: (loaded: number, total: number) => void,
    signal?: AbortSignal,
    fileName?: string
  ) => Promise<any>
  deleteItem: (itemId: string, itemType: 'file' | 'folder') => Promise<any>
  bulkDelete: (items: Array<{ id: string; type: 'file' | 'folder' }>) => Promise<void>
  renameItem: (itemId: string, newName: string, itemType: 'file' | 'folder') => Promise<void>
  moveItems: (items: Array<{ id: string; type: 'file' | 'folder' }>, newPath: string) => Promise<void>
  loadFolderStructure: () => Promise<any[]>
  toggleSelection: (itemId: string) => void
  clearSelection: () => void
  setViewMode: (mode: 'grid' | 'list') => void
  setSortBy: (field: 'name' | 'date' | 'size' | 'type') => void
  searchFiles: (query: string) => Promise<void>
  refreshStorageStats: () => Promise<void>
}

export const useFileStore = create<FileState>((set, get) => ({
  currentPath: '',
  items: [],
  isLoading: false,
  selectedItems: new Set(),
  viewMode: 'grid',
  sortBy: 'name',
  sortOrder: 'asc',

  loadItems: async (path?: string) => {
    const targetPath = path ?? get().currentPath
    set({ isLoading: true })

    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const response = await api.get<{ items?: FileSystemItem[] }>(`/api/list?path=${encodeURIComponent(targetPath)}`, token, csrfToken ?? undefined)

      if (response.success && response.data) {
        set({
          items: response.data.items || [],
          currentPath: targetPath,
          isLoading: false,
          selectedItems: new Set(),
        })
      } else {
        throw new Error(response.error || 'Failed to load items')
      }
    } catch (error) {
      set({ isLoading: false })
      
      if (error instanceof ApiError && error.status === 429) {
        set({ items: [] })
        return
      }
      
      console.error('Load items error:', error)
      useToastStore.getState().addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load files',
      })
    }
  },

  navigateToFolder: async (path: string) => {
    await get().loadItems(path)
  },

  createFolder: async (name: string, isPublic: boolean) => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const { currentPath } = get()
      
      const response = await api.post('/api/folders', {
        name,
        parentPath: currentPath,
        isPublic,
      }, token, csrfToken ?? undefined)

      if (response.success) {
        useToastStore.getState().addToast({
          type: 'success',
          message: `Folder "${name}" created successfully`,
        })
        
        await get().loadItems()
        await get().refreshStorageStats()
      } else {
        throw new Error(response.error || 'Failed to create folder')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        return
      }
      
      console.error('Create folder error:', error)
      useToastStore.getState().addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create folder',
      })
    }
  },

  uploadFile: async (file: File, isPublic: boolean, fileName?: string) => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const { currentPath } = get()

      const response = await api.uploadFile(file, currentPath, isPublic, token, fileName, csrfToken ?? undefined)

      if (response.success) {
        await get().loadItems()
        await get().refreshStorageStats()
        
        return response
      } else {
        throw new Error(response.error || 'Upload failed')
      }
    } catch (error) {
      console.error('Upload error:', error)
      throw error
    }
  },

  uploadFileWithProgress: async (
    file: File,
    isPublic: boolean,
    onProgress: (loaded: number, total: number) => void,
    signal?: AbortSignal,
    fileName?: string
  ) => {
    const { token, csrfToken } = useAuthStore.getState()
    if (!token) throw new Error('Not authenticated')

    const { currentPath } = get()

    const CHUNK_THRESHOLD = 50 * 1024 * 1024
    if (file.size > CHUNK_THRESHOLD) {
      return get().uploadChunkedFile(file, isPublic, onProgress, signal, fileName)
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const formData = new FormData()
      
      formData.append('file', file)
      formData.append('isPublic', isPublic.toString())
      formData.append('path', currentPath)
      if (fileName) {
        formData.append('fileName', fileName)
      }

      if (signal) {
        signal.addEventListener('abort', () => {
          xhr.abort()
          reject(new Error('Upload cancelled'))
        })
      }

      let lastTime = Date.now()
      let lastLoaded = 0

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const currentTime = Date.now()
          const timeDiff = currentTime - lastTime
          const bytesDiff = e.loaded - lastLoaded

          const progressInfo: UploadProgress = {
            loaded: e.loaded,
            total: e.total,
            percentage: Math.round((e.loaded / e.total) * 100),
            speed: timeDiff > 0 ? (bytesDiff / timeDiff) * 1000 : 0,
            eta: timeDiff > 0 ? ((e.total - e.loaded) / ((bytesDiff / timeDiff) * 1000)) : 0
          }

          onProgress(progressInfo.loaded, progressInfo.total)

          console.debug(`Upload: ${progressInfo.percentage}% at ${(progressInfo.speed / 1024).toFixed(1)} KB/s, ETA: ${progressInfo.eta.toFixed(1)}s`)

          lastTime = currentTime
          lastLoaded = e.loaded
        }
      })

      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            
            await get().loadItems()
            await get().refreshStorageStats()
            
            resolve(response)
          } catch (parseError) {
            reject(new Error('Invalid server response'))
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText)
            reject(new Error(errorResponse.error || `Upload failed: ${xhr.statusText}`))
          } catch {
            reject(new Error(`Upload failed: ${xhr.statusText}`))
          }
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'))
      })

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout'))
      })

      xhr.timeout = 30 * 60 * 1000

      xhr.open('POST', `${API_BASE_URL}/api/upload`)
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken)
      
      xhr.send(formData)
    })
  },

  uploadChunkedFile: async (
    file: File,
    isPublic: boolean,
    onProgress: (loaded: number, total: number) => void,
    signal?: AbortSignal,
    fileName?: string
  ) => {
    const { token, csrfToken } = useAuthStore.getState()
    if (!token) throw new Error('Not authenticated')

    const { currentPath } = get()
    const CHUNK_SIZE = 10 * 1024 * 1024
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
    
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    let uploadedBytes = 0

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        if (signal?.aborted) {
          throw new Error('Upload cancelled')
        }

        const start = chunkIndex * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, file.size)
        const chunk = file.slice(start, end)

        await uploadChunk(
          chunk,
          chunkIndex,
          totalChunks,
          uploadId,
          token,
          currentPath,
          isPublic,
          fileName || file.name,
          csrfToken ?? undefined
        )

        uploadedBytes += chunk.size
        onProgress(uploadedBytes, file.size)
      }

      const response = await finalizeChunkedUpload(uploadId, token, csrfToken ?? undefined)
      
      await get().loadItems()
      await get().refreshStorageStats()
      
      return response

    } catch (error) {
      try {
        await cleanupChunkedUpload(uploadId, token, csrfToken ?? undefined)
      } catch (cleanupError) {
        console.error('Failed to cleanup upload:', cleanupError)
      }
      throw error
    }
  },

  deleteItem: async (itemId: string, itemType: 'file' | 'folder') => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const response = await api.delete('/api/delete', {
        itemId,
        itemType,
      }, token, csrfToken ?? undefined)

      if (response.success) {
        await get().loadItems()
        await get().refreshStorageStats()
        
        const { selectedItems } = get()
        if (selectedItems.has(itemId)) {
          selectedItems.delete(itemId)
          set({ selectedItems: new Set(selectedItems) })
        }
        
        return response
      } else {
        throw new Error(response.error || 'Delete failed')
      }
    } catch (error) {
      console.error('Delete error:', error)
      throw error
    }
  },

  bulkDelete: async (items: Array<{ id: string; type: 'file' | 'folder' }>) => {
    const { token, csrfToken } = useAuthStore.getState()
    if (!token) throw new Error('Not authenticated')

    try {
      const response = await api.delete('/api/bulk-delete', {
        items,
      }, token, csrfToken ?? undefined)

      if (response.success) {
        useToastStore.getState().addToast({
          type: 'success',
          message: `Successfully deleted ${items.length} item${items.length !== 1 ? 's' : ''}`,
        })
        
        await get().loadItems()
        await get().refreshStorageStats()
        
        set({ selectedItems: new Set() })
      } else {
        throw new Error(response.error || 'Bulk delete failed')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        return
      }
      
      console.error('Bulk delete error:', error)
      useToastStore.getState().addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Bulk delete failed',
      })
    }
  },

  renameItem: async (itemId: string, newName: string, itemType: 'file' | 'folder') => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const response = await api.post('/api/rename', {
        itemId,
        newName,
        itemType,
      }, token, csrfToken ?? undefined)

      if (response.success) {
        useToastStore.getState().addToast({
          type: 'success',
          message: `${itemType === 'file' ? 'File' : 'Folder'} renamed successfully`,
        })
        
        await get().loadItems()
        await get().refreshStorageStats()
      } else {
        throw new Error(response.error || 'Rename failed')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        throw error
      }
      
      console.error('Rename error:', error)
      throw error
    }
  },

  moveItems: async (items: Array<{ id: string; type: 'file' | 'folder' }>, newPath: string) => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const response = await api.post('/api/move', {
        items,
        newPath,
      }, token, csrfToken ?? undefined)

      if (response.success) {
        useToastStore.getState().addToast({
          type: 'success',
          message: `Successfully moved ${items.length} item${items.length !== 1 ? 's' : ''}`,
        })
        
        await get().loadItems()
        await get().refreshStorageStats()
        
        set({ selectedItems: new Set() })
      } else {
        throw new Error(response.error || 'Move failed')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        throw error
      }
      
      console.error('Move error:', error)
      throw error
    }
  },

  loadFolderStructure: async (): Promise<any[]> => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const response = await api.get<{ folders?: unknown[] }>('/api/folders', token, csrfToken ?? undefined)

      if (response.success && response.data) {
        return response.data.folders || []
      } else {
        throw new Error(response.error || 'Failed to load folders')
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        return []
      }
      
      console.error('Load folder structure error:', error)
      throw error
    }
  },

  toggleSelection: (itemId: string) => {
    const { selectedItems } = get()
    const newSelection = new Set(selectedItems)
    
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId)
    } else {
      newSelection.add(itemId)
    }
    
    set({ selectedItems: newSelection })
  },

  clearSelection: () => {
    set({ selectedItems: new Set() })
  },

  setViewMode: (mode: 'grid' | 'list') => {
    set({ viewMode: mode })
  },

  setSortBy: (field: 'name' | 'date' | 'size' | 'type') => {
    const { sortBy, sortOrder } = get()
    
    const newOrder = field === sortBy && sortOrder === 'asc' ? 'desc' : 'asc'
    
    set({ sortBy: field, sortOrder: newOrder })
  },

  searchFiles: async (query: string) => {
    if (!query.trim()) {
      await get().loadItems()
      return
    }

    set({ isLoading: true })

    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) throw new Error('Not authenticated')

      const response = await api.get<{ results?: FileSystemItem[] }>(
        `/api/search?q=${encodeURIComponent(query)}`,
        token,
        csrfToken ?? undefined
      )

      if (response.success && response.data) {
        set({
          items: response.data.results || [],
          isLoading: false,
        })
      } else {
        throw new Error(response.error || 'Search failed')
      }
    } catch (error) {
      set({ isLoading: false })
      
      if (error instanceof ApiError && error.status === 429) {
        set({ items: [] })
        return
      }
      
      console.error('Search error:', error)
      useToastStore.getState().addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Search failed',
      })
    }
  },

  refreshStorageStats: async () => {
    try {
      const { token, csrfToken } = useAuthStore.getState()
      if (!token) return

      await api.getStorageUsage(token, csrfToken ?? undefined)
      
      if (typeof (window as any).refreshStorageUsage === 'function') {
        (window as any).refreshStorageUsage()
      }
    } catch (error) {
      console.error('Failed to refresh storage stats:', error)
    }
  },
}))

// Helper functions for chunked upload
async function uploadChunk(
  chunk: Blob,
  chunkIndex: number,
  totalChunks: number,
  uploadId: string,
  token: string,
  currentPath: string,
  isPublic: boolean,
  fileName: string,
  csrfToken?: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    
    formData.append('chunk', chunk)
    formData.append('chunkIndex', chunkIndex.toString())
    formData.append('totalChunks', totalChunks.toString())
    formData.append('uploadId', uploadId)
    formData.append('fileName', fileName)
    formData.append('path', currentPath)
    formData.append('isPublic', isPublic.toString())

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Chunk upload failed: ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during chunk upload'))
    })

    xhr.open('POST', `${API_BASE_URL}/api/upload-chunk`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken)
    xhr.send(formData)
  })
}

async function finalizeChunkedUpload(uploadId: string, token: string, csrfToken?: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/api/finalize-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-CSRF-Token': csrfToken || ''
    },
    body: JSON.stringify({ uploadId })
  })

  if (!response.ok) {
    throw new Error('Failed to finalize upload')
  }

  return response.json()
}

async function cleanupChunkedUpload(uploadId: string, token: string, csrfToken?: string): Promise<void> {
  await fetch(`${API_BASE_URL}/api/cleanup-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-CSRF-Token': csrfToken || ''
    },
    body: JSON.stringify({ uploadId })
  })
}